import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../sp-firebase/firebase';
import type { HedgePosition } from '../sp-types/positions';

export type { HedgePosition } from '../sp-types/positions';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

async function waitForFirebaseUser(timeoutMs: number = 5000): Promise<User | null> {
  if (auth.currentUser) {
    return auth.currentUser;
  }

  return new Promise((resolve) => {
    let resolved = false;
    let unsubscribe: (() => void) | null = null;

    const timer = setTimeout(() => {
      if (resolved) return;
      resolved = true;
      if (unsubscribe) {
        unsubscribe();
      }
      resolve(null);
    }, timeoutMs);

    unsubscribe = onAuthStateChanged(auth, (user) => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timer);
      if (unsubscribe) {
        unsubscribe();
      }
      resolve(user);
    });
  });
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = await waitForFirebaseUser();
  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

// HedgePosition type is defined in sp-types/positions.ts and re-exported above

export interface GetHedgePositionsParams {
  accountId?: number;
  formulaId?: number;
  startDate?: string;
  endDate?: string;
  status?: string;
  instrumentType?: string;
}

function getPreviousYearStart(): string {
  const now = new Date();
  const previousYear = now.getFullYear() - 1;
  return `${previousYear}-01-01`;
}

export async function getHedgePositions(
  params: GetHedgePositionsParams = {},
): Promise<HedgePosition[]> {
  const headers = await getAuthHeaders();
  const queryParams = new URLSearchParams();

  const startDate = params.startDate || getPreviousYearStart();

  if (params.accountId) queryParams.append('accountId', params.accountId.toString());
  if (params.formulaId) queryParams.append('formulaId', params.formulaId.toString());
  queryParams.append('startDate', startDate);
  if (params.endDate) queryParams.append('endDate', params.endDate);
  if (params.status) queryParams.append('status', params.status);
  if (params.instrumentType) queryParams.append('instrumentType', params.instrumentType);

  const url = `${API_URL}/api/v2/hedges/positions?${queryParams.toString()}`;

  const response = await fetch(url, { headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch hedge positions');
  }

  const data = await response.json();
  return (data.positions || []).map((p: any): HedgePosition => ({ ...p, category: 'hedge' as const }));
}

export interface DerivedHedgesResult {
  positions: HedgePosition[];
  allocations: TerminalAllocation[];
  /** Months a terminal's consumption lags its window (0 unless requested with withConsumptionLead). */
  consumptionLeadMonths: number;
}

export interface TerminalAllocation {
  terminal: string;
  startDate: string;
  endDate: string;
  materials: Record<string, {
    demand: number;
    buffer: number;
    supply: number;
    startStorage: number;
    endStorage: number;
    finalExposure: number;
    spotPrice: number;
    ratio: number;
    monetaryScore: number;
    allocationPct: number;
  }>;
  totalMonetaryExposure: number;
}

/**
 * Fetch backend-derived hedge positions for all derived materials.
 * Terminal-granularity allocation — single source of truth.
 */
export async function getDerivedHedges(
  accountId: number,
  pricesMap?: Record<string, number>,
  horizonMonths?: number,
  /** Apply the account's terminal consumption lead (accounts.settings.terminalConsumptionLeadMonths). */
  withConsumptionLead = false,
): Promise<DerivedHedgesResult> {
  const headers = await getAuthHeaders();
  const queryParams = new URLSearchParams();
  if (pricesMap && Object.keys(pricesMap).length > 0) {
    queryParams.append('pricesMap', JSON.stringify(pricesMap));
  }
  if (horizonMonths) queryParams.append('horizon', horizonMonths.toString());
  if (withConsumptionLead) queryParams.append('withConsumptionLead', 'true');

  const url = `${API_URL}/api/v2/hedge-derivation/${accountId}/derived-hedges?${queryParams.toString()}`;
  const response = await fetch(url, { headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch derived hedges');
  }

  const data = await response.json();
  const positions = (data.positions || []).map((p: any): HedgePosition => ({
    ...p,
    category: 'hedge' as const,
  }));
  return { positions, allocations: data.allocations || [], consumptionLeadMonths: Number(data.consumptionLeadMonths) || 0 };
}

/**
 * Fetch terminal allocation percentages for display in HedgesDeriving chart.
 */
export async function getTerminalAllocations(
  accountId: number,
  pricesMap?: Record<string, number>,
  horizonMonths?: number,
): Promise<TerminalAllocation[]> {
  const headers = await getAuthHeaders();
  const queryParams = new URLSearchParams();
  if (pricesMap && Object.keys(pricesMap).length > 0) {
    queryParams.append('pricesMap', JSON.stringify(pricesMap));
  }
  if (horizonMonths) queryParams.append('horizon', horizonMonths.toString());

  const url = `${API_URL}/api/v2/hedge-derivation/${accountId}/allocations?${queryParams.toString()}`;
  const response = await fetch(url, { headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch terminal allocations');
  }

  const data = await response.json();
  return data.allocations || [];
}
